import { toDateReadable, useFilter } from '@gov-nx/utils/common';
import { ServiceCode } from '../../../definitions/codes';
import { DEFAULT_START, ITEMS_PER_PAGE } from '../constants';
import {
	PPPStatusTabs,
	PPPTypeTabs,
	type PPPStatusTab,
	type PPPTypeTab,
} from '../types/tabs/tabs.types';
import { isSeznamStav, isSeznamTyp } from '../typeGuards';
import {
	resolveActiveTabValue,
	resolveDatesForType,
	resolveStatusForType,
} from '../utils';
import type { PPPSeznamFilterType } from '../types/filter/seznamFilter.types';
import type { FilterParam } from '@gov-nx/core/types';
import { usePPPLocale } from '../../usePPPLocale';

const initialState: PPPSeznamFilterType = {
	typSeznamu: 'AKTUALNI',
	stav: 'VSE',
	datumOd: undefined,
	datumDo: undefined,
	text: undefined,
	start: DEFAULT_START,
	pocet: ITEMS_PER_PAGE,
	stavByTyp: {
		[PPPTypeTabs.AKTUALNI]: PPPStatusTabs.VSE,
		[PPPTypeTabs.HISTORIE]: PPPStatusTabs.VSE,
	},
	datesByType: {
		[PPPTypeTabs.AKTUALNI]: {
			datumOd: undefined,
			datumDo: undefined,
		},
		[PPPTypeTabs.HISTORIE]: {
			datumOd: undefined,
			datumDo: undefined,
		},
	},
};

export const usePPPSeznamFilter = () => {
	const { t } = usePPPLocale();
	const filter = useFilter<PPPSeznamFilterType>({
		key: ServiceCode['PPP'],
		initialState,
	});
	const {
		stavByTyp: stavByTypFromFilter,
		datesByType: datesByTypeFromFilter,
		...queryFilter
	} = filter.filter;
	const statusByTypeFilter = stavByTypFromFilter ?? initialState.stavByTyp;
	const datesByTypeFilter = datesByTypeFromFilter ?? initialState.datesByType;

	const activeTypeTab: PPPTypeTab = resolveActiveTabValue(
		filter.filter.typSeznamu,
		isSeznamTyp,
		PPPTypeTabs.AKTUALNI
	);

	const rawStatusTab: PPPStatusTab = resolveActiveTabValue(
		filter.filter.stav,
		isSeznamStav,
		PPPStatusTabs.VSE
	);

	const activeStatusTab: PPPStatusTab = resolveStatusForType(
		activeTypeTab,
		rawStatusTab
	);

	const updateTabsFilter = (nextType: PPPTypeTab, nextStatus: PPPStatusTab) => {
		const normalizedStatus = resolveStatusForType(nextType, nextStatus);
		const currentDatesForType = resolveDatesForType(
			nextType,
			datesByTypeFilter
		);

		filter.setPartialFilter({
			typSeznamu: nextType,
			stav: normalizedStatus,
			start: DEFAULT_START,
			datumOd: currentDatesForType.datumOd,
			datumDo: currentDatesForType.datumDo,
			stavByTyp: {
				...statusByTypeFilter,
				[nextType]: normalizedStatus,
			},
			datesByType: {
				...datesByTypeFilter,
				[nextType]: {
					datumOd: currentDatesForType.datumOd,
					datumDo: currentDatesForType.datumDo,
				},
			},
		});
	};

	const onTypeTabChange = (nextType: PPPTypeTab) => {
		updateTabsFilter(nextType, statusByTypeFilter[nextType]);
	};

	const onStatusTabChange = (nextStatus: PPPStatusTab) => {
		updateTabsFilter(activeTypeTab, nextStatus);
	};

	const onSearchTextChange = (text?: string) => {
		filter.setPartialFilter({
			text,
			start: DEFAULT_START,
		});
	};

	const onDateRangeChange = (datumOd?: string, datumDo?: string) => {
		filter.setPartialFilter({
			datumOd,
			datumDo,
			start: DEFAULT_START,
			datesByType: {
				...datesByTypeFilter,
				[activeTypeTab]: {
					datumOd,
					datumDo,
				},
			},
		});
	};

	const onModalFiltersChange = ({
		datumOd,
		datumDo,
	}: {
		datumOd?: string;
		datumDo?: string;
	}) => {
		filter.setPartialFilter({
			datumOd,
			datumDo,
			start: DEFAULT_START,
			datesByType: {
				...datesByTypeFilter,
				[activeTypeTab]: {
					datumOd,
					datumDo,
				},
			},
		});
	};

	const goToPage = (page: number) => {
		filter.setPartialFilter({
			start: (page - 1) * filter.filter.pocet,
		});
	};

	const hasFilterChanges = () =>
		filter.hasFilterChanges({
			ignoredKeys: ['start', 'pocet', 'typSeznamu', 'stavByTyp', 'datesByType'],
		});

	const onFilterParamsReset = () =>
		onModalFiltersChange({
			datumOd: initialState.datumOd,
			datumDo: initialState.datumDo,
		});

	const filterParams: FilterParam[] = [
		{
			label: t('seznam.sluzby.filtr.params.label.datumOd'),
			value: queryFilter.datumOd
				? toDateReadable(queryFilter.datumOd)
				: undefined,
			isActive: !!queryFilter.datumOd,
			onRemove: () =>
				onDateRangeChange(initialState.datumOd, filter.filter.datumDo),
		},
		{
			label: t('seznam.sluzby.filtr.params.label.datumDo'),
			value: queryFilter.datumDo
				? toDateReadable(queryFilter.datumDo)
				: undefined,
			isActive: !!queryFilter.datumDo,
			onRemove: () =>
				onDateRangeChange(queryFilter.datumOd, initialState.datumDo),
		},
	];

	return {
		filter: queryFilter,
		filterParams,
		activeTypeTab,
		activeStatusTab,
		onTypeTabChange,
		onStatusTabChange,
		onSearchTextChange,
		onDateRangeChange,
		onModalFiltersChange,
		onFilterParamsReset,
		currentPage: filter.filter.start / filter.filter.pocet + 1,
		pageSize: filter.filter.pocet,
		goToPage,
		hasFilterChanges,
	};
};
